import { Injectable } from '@angular/core';
import { IShopGeoRegionItem, IShopOrderItem } from '@x/ecommerce/shop-client';
import { Maybe, ShopProductUnavailableReason } from '@x/schemas/ecommerce';
import { DateTime } from 'luxon';

export type IShopOrderItemAvailabilityStatus =
  | 'OUT_OF_STOCK'
  | 'UNAVAILABLE'
  | 'AVAILABLE'
  | 'UNLISTED';
export type IShopOrderItemAvailabilityView = {
  status: IShopOrderItemAvailabilityStatus;
  availableRegions?: IShopGeoRegionItem[];
  availableStock?: number | null;
  nextAvailableDate?: Date;
};

export interface IShopOrderItemView extends IShopOrderItem {
  availabilityView: IShopOrderItemAvailabilityView;
}

@Injectable()
export class OrderItemViewService {
  unlistedOrderItems: Maybe<ShopProductUnavailableReason>[] = [
    ShopProductUnavailableReason.Unlisted,
    ShopProductUnavailableReason.UnlistedRegion,
  ];

  constructor() {}

  makeView(orderItem: IShopOrderItem, nextAvailableDelayInDays: number): IShopOrderItemView {
    return {
      ...orderItem,
      availabilityView: this.availabilityStatus(orderItem, nextAvailableDelayInDays),
    };
  }

  makeList(
    orderItems: IShopOrderItem[],
    nextAvailableDelayInDays: number = 0,
  ): IShopOrderItemView[] {
    return orderItems.map((i) => this.makeView(i, nextAvailableDelayInDays));
  }

  private availabilityStatus(
    orderItem: IShopOrderItem,
    nextAvailableDelayInDays: number,
  ): IShopOrderItemAvailabilityView {
    const { availability } = orderItem;

    if (availability?.isAvailable === false) {
      if (this.unlistedOrderItems.includes(availability?.unavailableReason)) {
        return {
          status: 'UNLISTED',
          availableRegions: orderItem.availableRegions,
        };
      }

      if (availability?.unavailableReason === 'NO_STOCK') {
        return {
          status: 'OUT_OF_STOCK',
          availableStock: orderItem.availability?.nextAvailableQuantity,
        };
      }

      return { status: 'UNAVAILABLE' };
    }

    /*
     * only show next available date if more than 48hours
     * */

    const daysFromNow = DateTime.now().plus({ days: nextAvailableDelayInDays });
    const nextAvailableDate = DateTime.fromISO(orderItem.availability?.nextAvailableDate);
    const showDate = nextAvailableDate > daysFromNow;

    return {
      status: 'AVAILABLE',
      ...(showDate && { nextAvailableDate: orderItem.availability?.nextAvailableDate }),
    };
  }
}
